import { useRef, useEffect, useCallback, useState } from "react";
import {
  Bold, Italic, Underline, Strikethrough, Heading2, Heading3,
  List, ListOrdered, Quote, Link2, ImagePlus, RemoveFormatting, Loader2,
} from "lucide-react";
import styles from "./RichTextEditor.module.css";

/* Lightweight, dependency-free rich-text editor (contentEditable + execCommand).
   - Controlled via `value` (HTML string) / `onChange(html)` with no caret jumps.
   - Image upload is delegated to `onUploadImage(file) => Promise<url>` so the
     caller decides storage (OVH). Inserted images render as constrained thumbnails.
   - Paste is sanitised to plain text to keep stored HTML clean. */

const exec = (cmd, val = null) => {
  try { return document.execCommand(cmd, false, val); } catch { return false; }
};

const isEmptyHtml = (html) => {
  const h = (html || "").replace(/\s|&nbsp;/g, "");
  return h === "" || h === "<br>" || h === "<div><br></div>" || h === "<p><br></p>";
};

const escapeAttr = (s) => String(s).replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Start typing…",
  onUploadImage,
  disabled = false,
  minHeight = 170,
}) => {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const focusedRef = useRef(false);
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState({});

  // Push external value into the DOM only when it genuinely differs and the user
  // isn't actively editing — this is what prevents the caret from jumping.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = value || "";
    if (!focusedRef.current && el.innerHTML !== incoming) {
      el.innerHTML = incoming;
    }
  }, [value]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el || !onChange) return;
    const html = el.innerHTML;
    onChange(isEmptyHtml(html) ? "" : html);
  }, [onChange]);

  const refreshActive = useCallback(() => {
    if (disabled) return;
    const next = {};
    [["bold"], ["italic"], ["underline"], ["strikeThrough"], ["insertUnorderedList"], ["insertOrderedList"]].forEach(([c]) => {
      try { next[c] = document.queryCommandState(c); } catch { next[c] = false; }
    });
    setActive(next);
  }, [disabled]);

  const run = (cmd, val) => {
    if (disabled) return;
    ref.current?.focus();
    exec(cmd, val);
    emit();
    refreshActive();
  };

  const runBlock = (tag) => {
    if (disabled) return;
    ref.current?.focus();
    // toggle: if already that block, fall back to paragraph
    exec("formatBlock", `<${tag}>`);
    emit();
  };

  const handleLink = () => {
    if (disabled) return;
    const sel = window.getSelection?.();
    const hasSelection = sel && sel.toString().trim().length > 0;
    const url = window.prompt("Link URL (https://…)");
    if (!url) return;
    const safe = /^(https?:|mailto:|tel:)/i.test(url) ? url : `https://${url}`;
    ref.current?.focus();
    if (hasSelection) {
      exec("createLink", safe);
    } else {
      exec("insertHTML", `<a href="${escapeAttr(safe)}" target="_blank" rel="noopener noreferrer">${escapeAttr(safe)}</a>`);
    }
    emit();
  };

  const handleImagePick = () => { if (!disabled && !uploading) fileRef.current?.click(); };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!onUploadImage) return;
    if (!file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const url = await onUploadImage(file);
      if (url) {
        ref.current?.focus();
        exec("insertHTML", `<img src="${escapeAttr(url)}" class="rte-thumb" alt="note attachment" />&nbsp;`);
        emit();
      }
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = (e) => {
    if (disabled) return;
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData("text/plain");
    exec("insertText", text);
    emit();
  };

  const Btn = ({ cmd, block, onClick, title, children, isActive }) => (
    <button
      type="button"
      className={`${styles.tbBtn} ${isActive ? styles.tbBtnActive : ""}`}
      title={title}
      aria-label={title}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault() /* keep selection */}
      onClick={onClick || (() => (block ? runBlock(block) : run(cmd)))}
    >
      {children}
    </button>
  );

  return (
    <div className={`${styles.editor} ${disabled ? styles.disabled : ""}`}>
      <div className={styles.toolbar}>
        <div className={styles.tbGroup}>
          <Btn cmd="bold" title="Bold" isActive={active.bold}><Bold size={15} /></Btn>
          <Btn cmd="italic" title="Italic" isActive={active.italic}><Italic size={15} /></Btn>
          <Btn cmd="underline" title="Underline" isActive={active.underline}><Underline size={15} /></Btn>
          <Btn cmd="strikeThrough" title="Strikethrough" isActive={active.strikeThrough}><Strikethrough size={15} /></Btn>
        </div>
        <span className={styles.tbSep} />
        <div className={styles.tbGroup}>
          <Btn block="h2" title="Heading"><Heading2 size={15} /></Btn>
          <Btn block="h3" title="Subheading"><Heading3 size={15} /></Btn>
          <Btn block="blockquote" title="Quote"><Quote size={15} /></Btn>
        </div>
        <span className={styles.tbSep} />
        <div className={styles.tbGroup}>
          <Btn cmd="insertUnorderedList" title="Bullet points" isActive={active.insertUnorderedList}><List size={15} /></Btn>
          <Btn cmd="insertOrderedList" title="Numbered list" isActive={active.insertOrderedList}><ListOrdered size={15} /></Btn>
        </div>
        <span className={styles.tbSep} />
        <div className={styles.tbGroup}>
          <Btn title="Insert link" onClick={handleLink}><Link2 size={15} /></Btn>
          {onUploadImage && (
            <Btn title="Insert image" onClick={handleImagePick}>
              {uploading ? <Loader2 size={15} className={styles.spin} /> : <ImagePlus size={15} />}
            </Btn>
          )}
          <Btn title="Clear formatting" onClick={() => run("removeFormat")}><RemoveFormatting size={15} /></Btn>
        </div>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleImageChange} />
      </div>

      <div className={styles.surfaceWrap}>
        <div
          ref={ref}
          className={`${styles.surface} rte-surface`}
          style={{ minHeight }}
          contentEditable={!disabled}
          suppressContentEditableWarning
          spellCheck
          onInput={() => { emit(); }}
          onKeyUp={refreshActive}
          onMouseUp={refreshActive}
          onFocus={() => { focusedRef.current = true; refreshActive(); }}
          onBlur={() => { focusedRef.current = false; emit(); }}
          onPaste={handlePaste}
        />
        {isEmptyHtml(value) && !focusedRef.current && (
          <div className={styles.placeholder}>{placeholder}</div>
        )}
      </div>
    </div>
  );
};

export default RichTextEditor;
